import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { creditos, fecha, formatearCantidad, mgAGramos } from "../lib/formato";
import { Aviso, Cabecera, Campo, Cargando, Etiqueta, Modal, Panel, TarjetaDato, Vacio } from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

/**
 * Compras a proveedor: lo que se le encarga, lo que llega y lo que se le debe.
 *
 * Antes el proveedor era texto suelto dentro de cada lote, lo que se debía
 * vivía en una libreta, y el género entraba tecleándolo otra vez en Inventario
 * aunque acabara de escribirse el albarán.
 */

interface Proveedor {
  id: string;
  nombre: string;
  cif: string | null;
  telefono: string | null;
  email: string | null;
  activo: boolean;
  debeCentimos: number;
  _count: { compras: number };
}

interface Linea {
  id: string;
  descripcion: string;
  cantidadMg: number;
  costeCentimos: number;
  producto: { id: string; nombre: string } | null;
}

interface Compra {
  id: string;
  numero: number;
  estado: "PEDIDA" | "RECIBIDA" | "ANULADA";
  fecha: string;
  fechaRecepcion: string | null;
  totalCentimos: number;
  pagadoCentimos: number;
  referencia: string | null;
  notas: string | null;
  proveedor: { id: string; nombre: string };
  lineas: Linea[];
}

interface Respuesta {
  compras: Compra[];
  pendienteDePagar: number;
  pedidas: number;
}

interface Producto {
  id: string;
  nombre: string;
}

const ETIQUETA_ESTADO: Record<string, { texto: Clave; tipo: string }> = {
  PEDIDA: { texto: "co.pedida", tipo: "azul" },
  RECIBIDA: { texto: "co.recibida", tipo: "verde" },
  ANULADA: { texto: "co.anulada", tipo: "roja" },
};

export default function Compras({ pestanas, vista }: { pestanas?: ReactNode; vista: "compras" | "proveedores" }) {
  const { t, cuenta } = useIdioma();
  const { puede } = useSesion();
  const clienteConsultas = useQueryClient();
  const [nuevaCompra, setNuevaCompra] = useState(false);
  const [nuevoProveedor, setNuevoProveedor] = useState(false);
  const [pagando, setPagando] = useState<Compra | null>(null);
  /** La compra que se esta anulando, mientras el cuadro pide el motivo. */
  const [anulando, setAnulando] = useState<Compra | null>(null);
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");

  const compras = useQuery({ queryKey: ["compras"], queryFn: () => api<Respuesta>("/compras") });
  const proveedores = useQuery({
    queryKey: ["proveedores"],
    queryFn: () => api<Proveedor[]>("/compras/proveedores"),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ["compras"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["proveedores"] });
    // Recibir toca el stock y pagar toca la caja y los gastos.
    void clienteConsultas.invalidateQueries({ queryKey: ["productos"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["gastos"] });
  };

  async function recibir(compra: Compra) {
    setError("");
    try {
      const r = await api<{ lotesCreados: number }>(`/compras/${compra.id}/recibir`, { metodo: "POST" });
      setMensaje(
        r.lotesCreados > 0
          ? t("co.recibidaConLotes", {
              numero: compra.numero,
              lotes: cuenta(r.lotesCreados, "co.unLote", "co.lotes"),
            })
          : t("co.recibidaSinLotes", { numero: compra.numero }),
      );
      refrescar();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("co.falloRecibir"));
    }
  }

  if (compras.isLoading) return <Cargando />;
  const datos = compras.data;

  return (
    <>
      <Cabecera titulo={t("co.titulo")} descripcion={t("co.descripcion")}>
        {pestanas}
        {puede("gestionarInventario") &&
          (vista === "compras" ? (
            <button className="boton" onClick={() => setNuevaCompra(true)} disabled={(proveedores.data ?? []).length === 0}>
              {t("co.nuevaCompra")}
            </button>
          ) : (
            <button className="boton" onClick={() => setNuevoProveedor(true)}>
              {t("co.nuevoProveedor")}
            </button>
          ))}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>
      <Aviso tipo="ok">{mensaje}</Aviso>

      {anulando && (
        <DialogoAnularCompra
          compra={anulando}
          alCerrar={() => setAnulando(null)}
          alHecho={(numero) => {
            setAnulando(null);
            setError("");
            setMensaje(t("co.an.hecho", { numero }));
            refrescar();
          }}
          alFallar={(texto) => {
            setMensaje("");
            setError(texto);
          }}
        />
      )}

      {vista === "compras" && (proveedores.data ?? []).length === 0 && (
        <Aviso tipo="info">
          {t("co.antesDeLaPrimera")}
        </Aviso>
      )}

      <div className="rejilla cuatro" style={{ marginBottom: 16 }}>
        <TarjetaDato
          etiqueta={t("co.pendienteDePagar")}
          valor={creditos(datos?.pendienteDePagar ?? 0)}
          pie={t("co.aTodos")}
        />
        <TarjetaDato
          etiqueta={t("co.pedidasSinRecibir")}
          valor={datos?.pedidas ?? 0}
          pie={t("co.enCamino")}
        />
      </div>

      {vista === "proveedores" ? (
        <Panel sinCuerpo>
          {!proveedores.data || proveedores.data.length === 0 ? (
            <Vacio>{t("co.sinProveedores")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("co.proveedor")}</th>
                    <th>{t("co.contacto")}</th>
                    <th className="num">{t("co.compras")}</th>
                    <th className="num">{t("co.seLeDebe")}</th>
                  </tr>
                </thead>
                <tbody>
                  {proveedores.data.map((p) => (
                    <tr key={p.id} style={p.activo ? undefined : { opacity: 0.55 }}>
                      <td>
                        <strong>{p.nombre}</strong>
                        {p.cif && <div className="suave pequeno">{p.cif}</div>}
                      </td>
                      <td className="pequeno suave">
                        {[p.telefono, p.email].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td className="num tabular">{p._count.compras}</td>
                      <td className="num tabular">
                        {p.debeCentimos > 0 ? <strong>{creditos(p.debeCentimos)}</strong> : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      ) : !datos || datos.compras.length === 0 ? (
        <Panel sinCuerpo>
          <Vacio>{t("co.sinCompras")}</Vacio>
        </Panel>
      ) : (
        <div className="columna" style={{ gap: 12 }}>
          {datos.compras.map((c) => {
            const debe = c.totalCentimos - c.pagadoCentimos;
            const marca = ETIQUETA_ESTADO[c.estado]!;
            return (
              <Panel key={c.id} sinCuerpo>
                <div className="panel-cabecera">
                  <div>
                    <strong>
                      #{c.numero} · {c.proveedor.nombre}
                    </strong>
                    <div className="suave pequeno">
                      {fecha(c.fecha)}
                      {c.referencia && ` · ${c.referencia}`}
                      {c.fechaRecepcion && t("co.recibidaEl", { fecha: fecha(c.fechaRecepcion) })}
                    </div>
                  </div>
                  <div className="fila">
                    <Etiqueta tipo={marca.tipo}>{t(marca.texto)}</Etiqueta>
                    {c.estado !== "ANULADA" && (
                      <Etiqueta tipo={debe > 0 ? "ambar" : "verde"}>
                        {debe > 0 ? t("co.debe", { importe: creditos(debe) }) : t("co.pagada")}
                      </Etiqueta>
                    )}
                  </div>
                </div>

                <div className="panel-cuerpo">
                  <table className="tabla">
                    <tbody>
                      {c.lineas.map((l) => (
                        <tr key={l.id}>
                          <td>
                            {l.descripcion}
                            {!l.producto && <span className="suave pequeno">{t("co.noEsGenero")}</span>}
                          </td>
                          <td className="num tabular pequeno">
                            {l.cantidadMg > 0 ? formatearCantidad(l.cantidadMg, "PESO") : "—"}
                          </td>
                          <td className="num tabular">{creditos(l.costeCentimos)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={2}>
                          <strong>{t("m.total")}</strong>
                        </td>
                        <td className="num tabular">
                          <strong>{creditos(c.totalCentimos)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="fila" style={{ marginTop: 12, justifyContent: "flex-end" }}>
                    {c.estado === "PEDIDA" && puede("gestionarInventario") && (
                      <button className="boton" onClick={() => void recibir(c)}>
                        {t("co.haLlegado")}
                      </button>
                    )}
                    {c.estado !== "ANULADA" && debe > 0 && puede("gestionarGastos") && (
                      <button className="boton secundario" onClick={() => setPagando(c)}>
                        {t("co.pagar")}
                      </button>
                    )}
                    {/*
                     * Solo cuando de verdad se puede: pedida y sin un euro
                     * pagado. El servidor tiene tres negativas para lo demas
                     * -ya recibida, ya pagada, ya anulada-, y un boton que se
                     * come una negativa es un boton que no deberia estar.
                     */}
                    {c.estado === "PEDIDA" &&
                      c.pagadoCentimos === 0 &&
                      puede("gestionarInventario") && (
                        <button className="boton secundario" onClick={() => setAnulando(c)}>
                          {t("co.anular")}
                        </button>
                      )}
                  </div>
                </div>
              </Panel>
            );
          })}
        </div>
      )}

      {nuevoProveedor && (
        <DialogoProveedor
          alCerrar={() => setNuevoProveedor(false)}
          alFallar={setError}
          alHecho={() => {
            setNuevoProveedor(false);
            refrescar();
          }}
        />
      )}

      {nuevaCompra && (
        <DialogoCompra
          proveedores={proveedores.data ?? []}
          alCerrar={() => setNuevaCompra(false)}
          alFallar={setError}
          alHecho={() => {
            setNuevaCompra(false);
            refrescar();
          }}
        />
      )}

      {pagando && (
        <DialogoPago
          compra={pagando}
          alCerrar={() => setPagando(null)}
          alFallar={setError}
          alHecho={() => {
            setPagando(null);
            refrescar();
          }}
        />
      )}
    </>
  );
}

function DialogoProveedor({
  alCerrar,
  alHecho,
  alFallar,
}: {
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (m: string) => void;
}) {
  const { t } = useIdioma();
  const [datos, setDatos] = useState({ nombre: "", cif: "", telefono: "", email: "", notas: "" });
  const [enviando, setEnviando] = useState(false);
  const cambiar = (campo: string, valor: string) => setDatos((d) => ({ ...d, [campo]: valor }));

  return (
    <Modal
      titulo={t("co.p.titulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || !datos.nombre.trim()}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/compras/proveedores", { metodo: "POST", cuerpo: datos });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("co.p.fallo"));
                setEnviando(false);
              }
            }}
          >
            {enviando ? t("g.guardando") : t("g.guardar")}
          </button>
        </>
      }
    >
      <Campo etiqueta={t("co.p.nombre")}>
        <input value={datos.nombre} onChange={(e) => cambiar("nombre", e.target.value)} autoFocus />
      </Campo>
      <div className="linea-campos">
        <Campo etiqueta={t("co.p.cif")}>
          <input value={datos.cif} onChange={(e) => cambiar("cif", e.target.value)} />
        </Campo>
        <Campo etiqueta={t("co.p.telefono")}>
          <input value={datos.telefono} onChange={(e) => cambiar("telefono", e.target.value)} />
        </Campo>
      </div>
      <Campo etiqueta={t("co.p.email")}>
        <input value={datos.email} onChange={(e) => cambiar("email", e.target.value)} />
      </Campo>
      <Campo etiqueta={t("co.p.notas")}>
        <input value={datos.notas} onChange={(e) => cambiar("notas", e.target.value)} maxLength={500} />
      </Campo>
    </Modal>
  );
}

interface LineaNueva {
  productoId: string;
  descripcion: string;
  cantidadG: string;
  costeEuros: string;
}

const LINEA_VACIA: LineaNueva = { productoId: "", descripcion: "", cantidadG: "", costeEuros: "" };

function DialogoCompra({
  proveedores,
  alCerrar,
  alHecho,
  alFallar,
}: {
  proveedores: Proveedor[];
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (m: string) => void;
}) {
  const [proveedorId, setProveedorId] = useState(proveedores[0]?.id ?? "");
  const [referencia, setReferencia] = useState("");
  const [lineas, setLineas] = useState<LineaNueva[]>([{ ...LINEA_VACIA }]);
  const [enviando, setEnviando] = useState(false);

  const productos = useQuery({
    queryKey: ["productos"],
    queryFn: () => api<Producto[]>("/catalogo/productos"),
  });

  const cambiarLinea = (indice: number, campo: keyof LineaNueva, valor: string) =>
    setLineas((actual) => actual.map((l, i) => (i === indice ? { ...l, [campo]: valor } : l)));

  const { t } = useIdioma();
  const total = lineas.reduce((s, l) => s + (Number(l.costeEuros.replace(",", ".")) || 0), 0);
  const valido = proveedorId && lineas.every((l) => l.descripcion.trim() && Number(l.costeEuros) >= 0);

  return (
    <Modal
      titulo={t("co.n.titulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || !valido}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/compras", {
                  metodo: "POST",
                  cuerpo: {
                    proveedorId,
                    referencia: referencia.trim() || undefined,
                    lineas: lineas.map((l) => ({
                      productoId: l.productoId || null,
                      descripcion: l.descripcion.trim(),
                      cantidadG: Number(l.cantidadG.replace(",", ".")) || 0,
                      costeEuros: Number(l.costeEuros.replace(",", ".")) || 0,
                    })),
                  },
                });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("co.n.fallo"));
                setEnviando(false);
              }
            }}
          >
            {enviando
              ? t("g.guardando")
              : t("co.n.registrar", { importe: creditos(Math.round(total * 100)) })}
          </button>
        </>
      }
    >
      <div className="linea-campos">
        <Campo etiqueta={t("co.n.proveedor")}>
          <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)}>
            {proveedores.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </select>
        </Campo>
        <Campo etiqueta={t("co.n.referencia")} ayuda={t("co.n.referenciaAyuda")}>
          <input value={referencia} onChange={(e) => setReferencia(e.target.value)} maxLength={60} />
        </Campo>
      </div>

      <h3 style={{ marginBottom: 6 }}>{t("co.n.queSeCompra")}</h3>
      <p className="suave pequeno" style={{ marginTop: 0 }}>
        {t("co.n.explicacion")}
      </p>

      {lineas.map((linea, i) => (
        <div key={i} className="linea-campos">
          <Campo etiqueta={i === 0 ? t("co.n.producto") : ""}>
            <select
              value={linea.productoId}
              onChange={(e) => {
                cambiarLinea(i, "productoId", e.target.value);
                const p = (productos.data ?? []).find((x) => x.id === e.target.value);
                if (p && !linea.descripcion) cambiarLinea(i, "descripcion", p.nombre);
              }}
            >
              <option value="">{t("co.n.noEsGenero")}</option>
              {(productos.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etiqueta={i === 0 ? t("co.n.descripcion") : ""}>
            <input
              value={linea.descripcion}
              onChange={(e) => cambiarLinea(i, "descripcion", e.target.value)}
              maxLength={200}
            />
          </Campo>
          <Campo etiqueta={i === 0 ? t("co.n.gramos") : ""}>
            <input
              type="number"
              step="0.01"
              min={0}
              value={linea.cantidadG}
              onChange={(e) => cambiarLinea(i, "cantidadG", e.target.value)}
              disabled={!linea.productoId}
            />
          </Campo>
          <Campo etiqueta={i === 0 ? t("co.n.coste") : ""}>
            <input
              type="number"
              step="0.01"
              min={0}
              value={linea.costeEuros}
              onChange={(e) => cambiarLinea(i, "costeEuros", e.target.value)}
            />
          </Campo>
        </div>
      ))}

      <div className="fila">
        <button className="boton fantasma" onClick={() => setLineas((a) => [...a, { ...LINEA_VACIA }])}>
          {t("co.n.otraLinea")}
        </button>
        {lineas.length > 1 && (
          <button className="boton fantasma" onClick={() => setLineas((a) => a.slice(0, -1))}>
            {t("co.n.quitarUltima")}
          </button>
        )}
      </div>
    </Modal>
  );
}

function DialogoPago({
  compra,
  alCerrar,
  alHecho,
  alFallar,
}: {
  compra: Compra;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (m: string) => void;
}) {
  const { t } = useIdioma();
  const debe = compra.totalCentimos - compra.pagadoCentimos;
  const [importeEuros, setImporteEuros] = useState(String(debe / 100));
  const [metodoPago, setMetodoPago] = useState("TRANSFERENCIA");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("co.g.titulo", { numero: compra.numero })}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando}
            onClick={async () => {
              setEnviando(true);
              try {
                await api(`/compras/${compra.id}/pagar`, {
                  metodo: "POST",
                  cuerpo: { importeEuros: Number(importeEuros.replace(",", ".")), metodoPago },
                });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("co.g.fallo"));
                setEnviando(false);
              }
            }}
          >
            {enviando ? t("g.guardando") : t("co.g.registrar")}
          </button>
        </>
      }
    >
      <p style={{ marginTop: 0 }}>
        {t("co.g.quedanPorPagar", { importe: creditos(debe), proveedor: compra.proveedor.nombre })}
      </p>

      <div className="linea-campos">
        <Campo etiqueta={t("co.g.importe")} ayuda={t("co.g.aPlazos")}>
          <input
            type="number"
            step="0.01"
            min={0}
            value={importeEuros}
            onChange={(e) => setImporteEuros(e.target.value)}
            autoFocus
          />
        </Campo>
        <Campo etiqueta={t("co.g.formaDePago")}>
          <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
            {["EFECTIVO", "TARJETA", "TRANSFERENCIA", "DOMICILIADO"].map((m) => (
              <option key={m} value={m}>
                {t(`pago.${m}` as Clave)}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      <p className="suave pequeno">
        {t("co.g.comoGasto")} {metodoPago === "EFECTIVO" && t("co.g.enEfectivo")}
      </p>
    </Modal>
  );
}


/**
 * Anular una compra que se pidio y no ha llegado.
 *
 * El motivo es opcional aqui, al contrario que al anular una entrega, y la razon
 * es la diferencia de lo que se deshace: anular una entrega devuelve genero al
 * stock y dinero a la cartera de un socio, y eso hay que poder explicarlo.
 * Anular un pedido que no ha llegado ni se ha pagado no deshace nada; queda quien
 * lo hizo en el registro de actividad, y con eso basta.
 */
function DialogoAnularCompra({
  compra,
  alCerrar,
  alHecho,
  alFallar,
}: {
  compra: Compra;
  alCerrar: () => void;
  alHecho: (numero: number) => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("co.an.titulo", { numero: compra.numero })}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton peligro"
            disabled={enviando}
            onClick={async () => {
              setEnviando(true);
              try {
                await api(`/compras/${compra.id}/anular`, {
                  metodo: "POST",
                  cuerpo: { motivo: motivo.trim() || null },
                });
                alHecho(compra.numero);
              } catch (fallo) {
                // Sin cerrar: lo escrito se queda y se puede reintentar.
                alFallar(fallo instanceof Error ? fallo.message : t("co.an.fallo"));
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("co.an.confirmar")}
          </button>
        </>
      }
    >
      <p style={{ marginTop: 0 }}>{t("co.an.quePasa")}</p>
      <Campo etiqueta={t("co.an.motivo")}>
        <textarea
          rows={2}
          maxLength={300}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoFocus
        />
      </Campo>
    </Modal>
  );
}
